import React, { useEffect, useState } from 'react';
import play from "../assets/images/play.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faFacebook, faLinkedin, faSpotify, faInstagram, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import Footer from './Footer';
import FeaturedMusic from './FeaturedMusic';
import Header from './Header';
import EventHero from './EventHero';

// ── Helpers (defined before component) ──
const isVideo = (mediaUrl) => {
  if (!mediaUrl) return false;
  return ['.mp4', '.webm', '.ogg'].some(ext => mediaUrl.endsWith(ext));
};

const SOCIAL_LINKS = [
  { key: 'youtube_link',   icon: faYoutube   },
  { key: 'facebook_link',  icon: faFacebook  },
  { key: 'linkedin_link',  icon: faLinkedin  },
  { key: 'spotify_link',   icon: faSpotify   },
  { key: 'instagram_link', icon: faInstagram },
  { key: 'whatsapp_link',  icon: faWhatsapp  },
  { key: 'x_twitter_link', icon: faXTwitter  },
];

const Home = () => {
  const [musicData, setMusicData] = useState(null);
  const [albumData, setAlbumData] = useState(null);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [hero, setHero] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch homepage hero configuration (default vs event)
  useEffect(() => {
    const fetchHero = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/hero`);
        const data = await res.json();
        setHero(data.hero);
      } catch (err) {
        console.error("Error fetching hero settings:", err);
        setHero({ mode: 'default' });
      }
    };
    fetchHero();
  }, [baseUrl]);

  // Fetch home music (only needed for the default hero)
  useEffect(() => {
    if (!hero || hero.mode === 'event') return;
    window.scrollTo(0, 0);
    const fetchMusicData = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/music/homemusic`);
        const data = await res.json();
        setMusicData(data[0]);
      } catch (err) {
        console.error("Error fetching music data:", err);
      }
    };
    fetchMusicData();
  }, [baseUrl, hero]);

  // Fetch album once musicData is ready
  useEffect(() => {
    if (!musicData?.albumID) return;
    const fetchAlbumData = async () => {
      try {
        const res = await fetch(`${baseUrl}/api/album/${musicData.albumID}`);
        if (!res.ok) throw new Error('Album not found');
        const album = await res.json();
        setAlbumData(album);
      } catch (err) {
        console.error("Error fetching album data:", err);
      }
    };
    fetchAlbumData();
  }, [musicData, baseUrl]);

  if (!hero) {
    return <section className="relative bg-black min-h-screen w-full" />;
  }

  if (hero.mode === 'event') {
    return (
      <>
        <EventHero hero={hero} />
        <FeaturedMusic />
        <Footer />
      </>
    );
  }

  const isLoading = !musicData || !albumData;

  return (
    <>
      <Header />
      <section className="relative bg-black min-h-screen w-full overflow-hidden">

      {isLoading ? (
        /* ── Loading / Fallback ── */
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <h1 className="font-ettlora text-5xl md:text-8xl xl:text-[110px] text-white">
            Elayone Music
          </h1>
        </div>

      ) : (
        <>
          {/* ── Background Media ── */}
          {isVideo(musicData.image_video) ? (
            <video
              key={musicData.image_video}
              className="absolute inset-0 w-full h-screen object-cover transition-opacity duration-700"
              style={{ opacity: mediaLoaded ? 0.55 : 0 }}
              src={`${baseUrl}${musicData.image_video}`}
              autoPlay
              loop
              muted
              playsInline
              onCanPlay={() => setMediaLoaded(true)}
            />
          ) : (
            <img
              key={musicData.image_video}
              className="absolute inset-0 w-full h-screen object-cover transition-opacity duration-700"
              style={{ opacity: mediaLoaded ? 0.55 : 0 }}
              src={`${baseUrl}${musicData.image_video}`}
              alt="Background"
              loading="eager"
              onLoad={() => setMediaLoaded(true)}
            />
          )}

          {/* ── Gradient Overlays ── */}
          <div className="absolute inset-0 h-screen pointer-events-none"
            style={{ background: 'linear-gradient(to top, #000000 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.45) 100%)' }}
          />

          {/* ── Hero Content ── */}
          <div className={`relative z-10 flex flex-col items-center justify-center h-screen text-white text-center px-6 transition-opacity duration-700 ${mediaLoaded ? 'opacity-100' : 'opacity-0'}`}>

            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-8">
              <span className="block w-8 h-px bg-white/40" />
              <p className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-gray-300 font-medium">
                Latest Song
              </p>
              <span className="block w-8 h-px bg-white/40" />
            </div>

            {/* Song Title */}
            <h1 className="font-ettlora text-5xl md:text-[80px] lg:text-[96px] xl:text-[118px] leading-none tracking-tight mb-12">
              {musicData.name}
            </h1>

            {/* Play / Watch Now CTA */}
            <a 
              href={musicData.youtube_link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-3 mb-8"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-white/30
                flex items-center justify-center
                group-hover:bg-white group-hover:border-white transition-all duration-300">
                <img
                  alt="Play"
                  className="w-5 md:w-6 ml-1 group-hover:invert transition-all duration-300"
                  src={play}
                />
              </div>
              <span className="text-[11px] tracking-[0.35em] uppercase text-gray-300
                group-hover:text-white transition-colors duration-200">
                Watch Now
              </span>
            </a>

            {/* Thin divider */}
            <div className="w-px h-6 bg-white/20 mb-6" />

            {/* Social Icons */}
            <div className="flex items-center gap-5 md:gap-6">
              {SOCIAL_LINKS.map(({ key, icon }) =>
                musicData[key] ? (
                  <a
                    key={key}
                    href={musicData[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-white transition-all duration-200 hover:scale-110 text-lg md:text-xl"
                  >
                    <FontAwesomeIcon icon={icon} />
                  </a>
                ) : null
              )}
            </div>

          </div>
        </>
      )}

      <FeaturedMusic />
      <Footer />
      </section>
    </>
  );
};

export default Home;